/**
 * Working-day arithmetic, holiday-aware.
 *
 * A sprint that says "day 3 of 10" while half the team is on Eid is lying about
 * how much week is left, and the "1 week / 2 weeks" picker that ends a sprint on
 * a public holiday commits work to a day nobody is there. So every function here
 * takes the holiday list as an argument.
 *
 * Holidays arrive as ISO `YYYY-MM-DD` strings because this package may not do
 * I/O: which country's calendar applies, and whether a moon-sighted date has
 * been corrected, are questions for the caller with the database.
 *
 * ── WHY THERE IS NO `Date` IN THIS FILE ─────────────────────────────────────
 * The lint config forbids the global outright, and rightly: `new Date(iso)` is
 * the single most common way a pure module quietly starts depending on where the
 * process runs. Everything below is integer arithmetic on a day number — days
 * since 1970-01-01 — using Howard Hinnant's civil-date algorithms. It has no
 * timezone, no locale and no clock, so a sprint that spans a daylight-saving
 * change counts the same number of days in Sydney as in Karachi.
 */
/**
 * Ten years. Every loop here walks a day at a time, and the inputs come from a
 * user-editable date field: a sprint accidentally ending in 2306 would otherwise
 * spin the event loop for minutes rather than failing.
 */
const MAX_DAYS = 3650;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Days from 1970-01-01 to a proleptic Gregorian y/m/d. Hinnant's `days_from_civil`. */
function daysFromCivil(year, month, day) {
    const y = year - (month <= 2 ? 1 : 0);
    const era = Math.floor(y / 400);
    const yearOfEra = y - era * 400;
    const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146_097 + dayOfEra - 719_468;
}
/** The inverse. Hinnant's `civil_from_days`, formatted straight back to ISO. */
function isoFromDays(dayNumber) {
    const z = dayNumber + 719_468;
    const era = Math.floor(z / 146_097);
    const dayOfEra = z - era * 146_097;
    const yearOfEra = Math.floor((dayOfEra -
        Math.floor(dayOfEra / 1460) +
        Math.floor(dayOfEra / 36_524) -
        Math.floor(dayOfEra / 146_096)) /
        365);
    const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
    const monthIndex = Math.floor((5 * dayOfYear + 2) / 153);
    const day = dayOfYear - Math.floor((153 * monthIndex + 2) / 5) + 1;
    const month = monthIndex + (monthIndex < 10 ? 3 : -9);
    const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * ISO string to day number, rejecting anything that is not a real date.
 *
 * The round-trip is the validation: 2026-02-30 parses fine as three integers and
 * `daysFromCivil` will happily place it, so the only honest check is whether the
 * day number formats back to what was passed in.
 */
function toDayNumber(isoDate) {
    const match = ISO_DATE.exec(isoDate);
    if (!match)
        throw new RangeError(`expected an ISO YYYY-MM-DD date, got ${isoDate}`);
    const dayNumber = daysFromCivil(Number(match[1]), Number(match[2]), Number(match[3]));
    if (isoFromDays(dayNumber) !== isoDate)
        throw new RangeError(`not a real date: ${isoDate}`);
    return dayNumber;
}
/** 1970-01-01 was a Thursday, which is what the +4 puts back. 0 is Sunday. */
function weekday(dayNumber) {
    return (((dayNumber + 4) % 7) + 7) % 7;
}
function isWeekend(dayNumber) {
    const day = weekday(dayNumber);
    return day === 0 || day === 6;
}
/** Mon–Fri, and not one of the days the workspace observes as a holiday. */
export function isWorkingDay(isoDate, holidays) {
    return !isWeekend(toDayNumber(isoDate)) && !holidays.includes(isoDate);
}
/**
 * Working days from `startsOn` to `endsOn`, counting both ends. Zero when the
 * range runs backwards, so a half-entered date form reads as "no capacity"
 * rather than throwing at the caller.
 */
export function workingDaysBetween(startsOn, endsOn, holidays) {
    const start = toDayNumber(startsOn);
    const end = toDayNumber(endsOn);
    if (end < start)
        return 0;
    if (end - start > MAX_DAYS)
        throw new RangeError(`range ${startsOn}..${endsOn} exceeds ${MAX_DAYS} days`);
    const lost = new Set(holidays);
    let count = 0;
    for (let cursor = start; cursor <= end; cursor += 1) {
        if (!isWeekend(cursor) && !lost.has(isoFromDays(cursor)))
            count += 1;
    }
    return count;
}
/**
 * "Day 3 of 10" — working days, because nobody counts the weekend of a sprint.
 * Null outside the sprint's dates, and null for a range that holds no working
 * days at all, where "day 0 of 0" would be worse than saying nothing.
 *
 * `Math.max(day, 1)` keeps the first working day reading as day 1 even when
 * today is the weekend that precedes it inside the range.
 */
export function workingDayOf(startsOn, endsOn, today, holidays) {
    if (today < startsOn || today > endsOn)
        return null;
    const start = toDayNumber(startsOn);
    const end = toDayNumber(endsOn);
    if (end - start > MAX_DAYS)
        throw new RangeError(`range ${startsOn}..${endsOn} exceeds ${MAX_DAYS} days`);
    const lost = new Set(holidays);
    let of = 0;
    let day = 0;
    for (let cursor = start; cursor <= end; cursor += 1) {
        const iso = isoFromDays(cursor);
        if (isWeekend(cursor) || lost.has(iso))
            continue;
        of += 1;
        if (iso <= today)
            day = of;
    }
    return of === 0 ? null : { day: Math.max(day, 1), of };
}
/**
 * The date the `count`-th working day falls on, counting `startsOn` itself when
 * it is one. This is what turns "2 weeks" in the sprint-length picker into an
 * end date, so a public holiday inside the sprint pushes the end out by a day
 * rather than quietly costing the team a day of the commitment.
 */
export function endDateForWorkingDays(startsOn, count, holidays) {
    if (!Number.isInteger(count) || count < 1) {
        throw new RangeError(`count must be a positive whole number of working days, got ${count}`);
    }
    const start = toDayNumber(startsOn);
    const lost = new Set(holidays);
    let seen = 0;
    for (let cursor = start; cursor <= start + MAX_DAYS; cursor += 1) {
        const iso = isoFromDays(cursor);
        if (isWeekend(cursor) || lost.has(iso))
            continue;
        seen += 1;
        if (seen === count)
            return iso;
    }
    throw new RangeError(`no ${count}th working day within ${MAX_DAYS} days of ${startsOn}`);
}
/** The first working day on or after `isoDate`; `isoDate` itself when it is one. */
export function nextWorkingDay(isoDate, holidays) {
    const start = toDayNumber(isoDate);
    const lost = new Set(holidays);
    for (let cursor = start; cursor <= start + MAX_DAYS; cursor += 1) {
        const iso = isoFromDays(cursor);
        if (!isWeekend(cursor) && !lost.has(iso))
            return iso;
    }
    throw new RangeError(`no working day within ${MAX_DAYS} days of ${isoDate}`);
}
/**
 * The holidays this range actually pays for, sorted and deduplicated.
 *
 * A holiday landing on a Saturday costs nothing, so it is not reported: this
 * feeds "3 working days lost to holidays" on a sprint card, and counting the
 * Saturday would make the card disagree with `workingDaysBetween`.
 */
export function holidaysWithin(startsOn, endsOn, holidays) {
    if (endsOn < startsOn)
        return [];
    const within = holidays.filter((holiday) => holiday >= startsOn && holiday <= endsOn && !isWeekend(toDayNumber(holiday)));
    return [...new Set(within)].toSorted();
}
//# sourceMappingURL=workdays.js.map